import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Snackbar,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Add, ContentCopy } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../constants/constants";
import MuPb from "../widgets/MuPb";
import BackBtn from "../widgets/BackBtn";

const ManageBanners = () => {
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [copySnackbar, setCopySnackbar] = useState(false);

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINT}admin/banners`, {
        ...getHeader(),
      });
      if (!response.ok) throw new Error("Failed to fetch banners");

      const data = await response.json();
      setBanners(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINT}admin/banners/${deleteDialog.id}`,
        {
          method: "DELETE",
          ...getHeader(),
        }
      );

      if (!response.ok) throw new Error("Failed to delete banner");

      await fetchBanners();
      setDeleteDialog({ open: false, id: null });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCopyImageUrl = async (imageUrl) => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      setCopySnackbar(true);
    } catch (err) {
      setError("Failed to copy image URL");
    }
  };

  const handleEdit = (id) => {
    navigate(`/home/${id}/edit-banner`);
  };

  const handleAdd = () => {
    navigate("/home/add-banner");
  };

  if (loading) {
    return <MuPb />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <BackBtn />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Manage Banners
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
          Add Banner
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Image URL</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {banners.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ py: 3 }}
                  >
                    No banners found. Click "Add Banner" to create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              banners.map((banner) => (
                <TableRow key={banner._id} hover>
                  <TableCell>
                    <Avatar
                      src={banner.imageUrl}
                      alt={banner.title}
                      variant="rounded"
                      sx={{ width: 60, height: 60 }}
                    />
                  </TableCell>
                  <TableCell>{banner.title}</TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 250,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {banner.imageUrl}
                      </Typography>
                      <Tooltip title="Copy image URL">
                        <IconButton
                          size="small"
                          onClick={() => handleCopyImageUrl(banner.imageUrl)}
                          sx={{ ml: 1 }}
                        >
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(banner._id)}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setDeleteDialog({ open: true, id: banner._id })
                      }
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this banner? This action cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        message="Image URL copied to clipboard"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Container>
  );
};

export default ManageBanners;
